import { RuleValueStore, RuleValueStoreMap, SerializedRuleValueStore } from "./RuleValueStore";
import { PublicStudentRulesDTO, StudentRulesDTO } from "./StudentRulesDTO";
import { PUBLIC_RULE_TYPES, PublicRuleTypeLiteral, RuleTypeLiteral } from "../../domain/RuleValue";
import { RuleTypeEnum } from "../../domain/RuleType";
import { EnrichedRule } from "../../domain/EnrichedRule";

export class StudentRules {
    public readonly updatedAt: Date;
    public readonly rules: {
        [K in RuleTypeLiteral]: InstanceType<typeof RuleValueStoreMap[K]>;
    };

    constructor(enrichedRules: EnrichedRule[], updatedAt: Date = new Date()) {
        this.updatedAt = updatedAt;

        const rulesInternal = Object.fromEntries(
            (Object.values(RuleTypeEnum) as RuleTypeLiteral[]).map(type => {
                const StoreClass = RuleValueStoreMap[type];
                return [type, new StoreClass()] as const;
            })
        ) as { [K in RuleTypeLiteral]: InstanceType<typeof RuleValueStoreMap[K]> };

        this.rules = rulesInternal;

        for (const enrichedRule of enrichedRules) {
            const type = enrichedRule.type.getValue();

            (this.rules[type] as RuleValueStore<RuleTypeLiteral>).add(enrichedRule.enrichedValue);
        }
    }

    public toDTO(): StudentRulesDTO {
        const serializedRulesInternal = {} as Record<RuleTypeLiteral, SerializedRuleValueStore<RuleTypeLiteral>>;
        const ruleTypes = Object.values(RuleTypeEnum) as RuleTypeLiteral[];

        for (const type of ruleTypes) {
            const store = this.rules[type];
            serializedRulesInternal[type] = store.serialize();
        }

        return {
            updatedAt: this.updatedAt.toISOString(),
            rules: serializedRulesInternal as { [K in RuleTypeLiteral]: SerializedRuleValueStore<K> },
        };
    }

    public toPublicDTO(): PublicStudentRulesDTO {
        const serializedRulesInternal = {} as Record<PublicRuleTypeLiteral, SerializedRuleValueStore<PublicRuleTypeLiteral>>;

        for (const type of PUBLIC_RULE_TYPES) {
            const store = this.rules[type];
            serializedRulesInternal[type] = store.serialize();
        }

        return {
            updatedAt: this.updatedAt.toISOString(),
            rules: serializedRulesInternal as { [K in PublicRuleTypeLiteral]: SerializedRuleValueStore<K> },
        };
    }

    static fromDTO(dto: StudentRulesDTO): StudentRules {
        const instance = new StudentRules([], new Date(dto.updatedAt));

        const ruleTypes = Object.values(RuleTypeEnum) as RuleTypeLiteral[];

        const rulesInternal = instance.rules as Record<RuleTypeLiteral, RuleValueStore<RuleTypeLiteral>>;

        for (const type of ruleTypes) {
            const serializedStore = dto.rules[type];
            if (serializedStore) {
                rulesInternal[type].fromSerialized(serializedStore);
            }
        }

        return instance;
    }
}

export class PublicStudentRules {
    public readonly updatedAt: Date;
    public readonly rules: {
        [K in PublicRuleTypeLiteral]: InstanceType<typeof RuleValueStoreMap[K]>;
    };

    constructor(dto: PublicStudentRulesDTO) {
        this.updatedAt = new Date(dto.updatedAt);

        // 1. Initialize the stores for each public rule type
        const rulesInternal = Object.fromEntries(
            PUBLIC_RULE_TYPES.map((type) => {
                const StoreClass = RuleValueStoreMap[type];
                const store = new StoreClass();

                // 2. Hydrate the store if data exists in the DTO
                const serializedStore = dto.rules[type];
                if (serializedStore) {
                    store.fromSerialized(serializedStore as any);
                }

                return [type, store] as const;
            })
        ) as { [K in PublicRuleTypeLiteral]: InstanceType<typeof RuleValueStoreMap[K]> };

        this.rules = rulesInternal;
    }

    static fromPrivateDTOToPublicDTO(privateDTO: StudentRulesDTO): PublicStudentRulesDTO {
        const publicRules = {} as Record<PublicRuleTypeLiteral, SerializedRuleValueStore<PublicRuleTypeLiteral>>;

        // Iterate only through the types allowed for public consumption
        for (const type of PUBLIC_RULE_TYPES) {
            const serializedStore = privateDTO.rules[type as RuleTypeLiteral];

            if (serializedStore) {
                // Map the private rule store to the public rules object
                publicRules[type] = serializedStore as SerializedRuleValueStore<PublicRuleTypeLiteral>;
            }
        }

        return {
            updatedAt: privateDTO.updatedAt,
            rules: publicRules as { [K in PublicRuleTypeLiteral]: SerializedRuleValueStore<K> },
        };
    }
}